// Reusable Y-axis fitting for line charts (introduced for IoT sensors in
// DOF-1348). Mirrors dofarm-mobile lib/helpers/chart/min_span_y_axis.dart —
// keep the two in sync.

/** Rounds up to a readable step: 1, 2, 2.5, 5 × 10^n. */
export const niceStep = (x: number) => {
  if (x <= 0) return 0.5
  const mag = Math.pow(10, Math.floor(Math.log10(x)))
  const norm = x / mag
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10
  return nice * mag
}

export interface MinSpanYAxisOptions {
  /** Smallest axis span, in the data's unit — keeps a near-flat line from being stretched. */
  minSpan: number
  /** Points actually plotted; below `minPoints` the caller keeps its default axis. Defaults to values.length. */
  pointCount?: number
  /** Fewest points needed to fit the axis (default 2). */
  minPoints?: number
  /** Headroom multiplier on the data swing (default 1.2). */
  paddingRatio?: number
  /** Aimed-for number of grid steps (default 5). */
  targetSteps?: number
  /** Don't let the axis go below 0 when all data is non-negative (default true). */
  clampAtZero?: boolean
}

export interface ChartYAxis {
  min: number
  max: number
  interval: number
  /** True when the swing was smaller than `minSpan` and the span was widened. */
  usedMinSpan: boolean
}

const EPS = 1e-9

/**
 * span = max(swing × paddingRatio, minSpan), centred on the data, then snapped
 * outward to a nice step (span / targetSteps). Returns null when there are too
 * few points, so the chart keeps its default axis.
 *
 * e.g. minSpan 0.5: 8.13–8.14 → 7.8–8.4 (step 0.1); 5.00–8.98 → 4–10 (step 1).
 */
export const getMinSpanYAxis = (
  values: number[],
  {
    minSpan,
    pointCount = values.length,
    minPoints = 2,
    paddingRatio = 1.2,
    targetSteps = 5,
    clampAtZero = true,
  }: MinSpanYAxisOptions
): ChartYAxis | null => {
  if (pointCount < minPoints || values.length === 0) return null
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  const padded = (hi - lo) * paddingRatio
  const usedMinSpan = padded < minSpan
  const span = usedMinSpan ? minSpan : padded
  const mid = (lo + hi) / 2
  const interval = niceStep(span / targetSteps)
  // EPS absorbs float noise (8.0 / 0.1 = 79.999…) so bounds don't jump a step.
  let min = Math.floor((mid - span / 2) / interval + EPS) * interval
  const max = Math.ceil((mid + span / 2) / interval - EPS) * interval
  if (clampAtZero && lo >= 0 && min < 0) min = 0
  // Strip float noise (e.g. 6.6000000000000005) so axis labels stay clean.
  const round = (v: number) => Number(v.toFixed(6))
  return { min: round(min), max: round(max), interval: round(interval), usedMinSpan }
}
